import { zodResolver } from '@hookform/resolvers/zod';
import bytes from 'bytes';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { z } from 'zod';
import { KTSVG } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import {
  CategorizeServiceSection,
  FieldStatus,
  keywordLabel,
  KeywordType,
  sexualCategoryLabel,
  SiService,
  siteIntelligenceServiceLabel,
} from '../../../mappings';
import { UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { useSaveCapture } from '../hooks/useSaveCapture';
import { urlToFile } from '../utils';
import { schema } from './Schema';
import PreviewDropzoneWithPaste from '../../../../../modules/components/dropzone/PreviewDropzoneWithPaste';

interface Props {
  data: {
    [SiService.ADULT_SHIELD_SERVICE]: any;
    [SiService.TOBACCO_SIFT_SERVICE]: any;
    [SiService.MEMBERS_ONLY_SIFT_SERVICE]: any;
    [SiService.CATEGORIZE_SERVICE]: any;
  };
  onClose: () => void;
  onUpdate: UsePageDataManagerResult['updateField'];
  onSaveMultiple: UsePageDataManagerResult['saveMultipleServices'];
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  defaultScreenshotUrl?: string;
}

const AddCategory = ({
  data,
  onClose,
  onUpdate,
  onSaveMultiple,
  isLoading,
  setIsLoading,
  defaultScreenshotUrl,
}: Props) => {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const { setToast } = useToast();

  const { saveCapture } = useSaveCapture();
  const [validateSchema, setValidateSchema] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [defaultScreenshotFile, setDefaultScreenshotFile] = useState<File | null>(null);
  const acceptedFileTypes = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
  };
  const acceptedFileSize = 10 * 1024 * 1024;

  const { categorySchema, sexualCategoriesSchema, tobaccoSchema, categorizeSchema } = schema(intl);

  const defaultSexualCategories: z.infer<typeof sexualCategoriesSchema> = {
    url: '',
    sexualCategories: [
      {
        name: '',
        screenshot: null,
      },
    ],
  };
  const defaultTobacco: z.infer<typeof tobaccoSchema> = {
    url: '',
    screenshot: null,
    brands: [],
  };
  const defaultCategorize: z.infer<typeof categorizeSchema> = {
    url: '',
    screenshot: null,
    keywords: [],
  };
  const defaultValues = {
    category: '',
    ...defaultSexualCategories,
    ...defaultTobacco,
    ...defaultCategorize,
  };
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    register,
    getFieldState,
    resetField,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validateSchema),
    defaultValues,
  });
  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
    control,
    name: 'sexualCategories',
  });

  const selectedCategory = watch('category');
  const isKeywordCategory = KeywordType?.[selectedCategory] !== undefined;

  useEffect(() => {
    if (!defaultScreenshotUrl) {
      setDefaultScreenshotFile(null);
      return;
    }

    let isMounted = true;

    void (async () => {
      const file = await urlToFile(defaultScreenshotUrl);
      if (!isMounted) return;
      setDefaultScreenshotFile(file);
    })();

    return () => {
      isMounted = false;
    };
  }, [defaultScreenshotUrl]);

  useEffect(() => {
    const shouldPrefillScreenshot =
      selectedCategory === SiService.TOBACCO_SIFT_SERVICE || isKeywordCategory;
    if (!shouldPrefillScreenshot || !defaultScreenshotFile) {
      return;
    }

    if (getValues('screenshot') instanceof File) {
      return;
    }

    setValue('screenshot', defaultScreenshotFile, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [defaultScreenshotFile, getValues, isKeywordCategory, selectedCategory, setValue]);

  useEffect(() => {
    if (selectedCategory !== SiService.ADULT_SHIELD_SERVICE || !defaultScreenshotFile) {
      return;
    }

    fields.forEach((_, index) => {
      const path = `sexualCategories.${index}.screenshot` as const;
      if (getValues(path) instanceof File) {
        return;
      }

      setValue(path, defaultScreenshotFile, {
        shouldDirty: false,
        shouldValidate: true,
      });
    });
  }, [defaultScreenshotFile, fields, getValues, selectedCategory, setValue]);

  useEffect(() => {
    if (!selectedCategory || selectedCategory === SiService.MEMBERS_ONLY_SIFT_SERVICE) {
      resetField('url');
      setValidateSchema(categorySchema);
    } else if (selectedCategory === SiService.ADULT_SHIELD_SERVICE) {
      setValidateSchema(categorySchema.and(sexualCategoriesSchema));
    } else if (selectedCategory === SiService.TOBACCO_SIFT_SERVICE) {
      setValidateSchema(categorySchema.and(tobaccoSchema));
    } else if (isKeywordCategory) {
      resetField('keywords');
      setValidateSchema(categorySchema.and(categorizeSchema));
    }
  }, [selectedCategory]);

  const categoryOptions = [
    ...[
      SiService.ADULT_SHIELD_SERVICE,
      SiService.TOBACCO_SIFT_SERVICE,
      SiService.MEMBERS_ONLY_SIFT_SERVICE,
    ].map((key) => ({
      value: key,
      label: siteIntelligenceServiceLabel[key],
    })),
    ...Object.keys(KeywordType)
      .filter((key) => key !== KeywordType.ADULT)
      .map((key) => ({
        value: key,
        label: keywordLabel[key],
      })),
  ];

  const sexualCategoryOptions = Object.entries(sexualCategoryLabel).map(([key, label]) => ({
    value: key,
    label: intl.formatMessage({ id: label }),
  }));

  const cubanBrandsList = useQuery(
    ['site-intelligence-tobacco-brands-list', selectedCategory],
    async () => {
      const { result, status } = await api(
        `admin/v1/list_tobaccos`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            filters: {},
            sort_order: { name: 'name', sort: 1 }, // sort by brand name
            offset: 0,
            limit: 99999,
          }),
        },
        { token: auth?.accessToken, refresh },
        'site-intelligence',
      ).then(async (resp) => {
        const result = await resp?.json();
        return { result: result, status: result?.code ?? resp?.status };
      });
      if (status === 200) return result;
    },
    { enabled: selectedCategory === SiService.TOBACCO_SIFT_SERVICE }
  );
  const cubanBrandOptions = useMemo(() => {
    const data = cubanBrandsList?.data?.results?.data;
    if (!(data?.length > 0)) return [];
    return data.map((brand) => ({
      value: brand?.name,
      label: brand?.name,
      __isNew__: false,
    }));
  }, [cubanBrandsList]);

  const categorizeKeywordsList = useQuery(
    ['site-intelligence-categorize-keywords-list', selectedCategory],
    async () => {
      const { result, status } = await api(
        `admin/v1/list_keywords`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            filters: {
              type: selectedCategory,
              is_deleted: false,
            },
            sort_order: { name: 'keyword', sort: 1 }, // sort by brand name
            offset: 0,
            limit: 99999,
          }),
        },
        { token: auth?.accessToken, refresh },
        'site-intelligence',
      ).then(async (resp) => {
        const result = await resp?.json();
        return { result: result, status: result?.code ?? resp?.status };
      });
      if (status === 200) return result;
    },
    { enabled: isKeywordCategory }
  );
  const categorizeKeywordOptions = useMemo(() => {
    const data = categorizeKeywordsList?.data?.results?.data;
    if (!(data?.length > 0)) return [];
    return data.map((keyword) => ({
      value: keyword?.keyword,
      label: keyword?.keyword,
      __isNew__: false,
    }));
  }, [categorizeKeywordsList]);

  const handleClose = () => {
    reset();
    setErrorMessage('');
    onClose();
  };

  const handleAddNewCubanBrand = async (brand: string) => {
    try {
      const payload = {
        name: brand,
      };

      const { result, status } = await api(
        `admin/v1/add_cuban_cigar_brand`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        },
        { token: auth?.accessToken, refresh },
        'site-intelligence',
      ).then(async (resp) => {
        const result = await resp?.json();
        return { result: result, status: result?.code ?? resp?.status };
      });
      const resolvedResult = await result;
      if (status === 200 && resolvedResult.code === 200 && resolvedResult.error === false) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      return false;
    }
  };

  const handleAddNewKeyword = async (keyword: string, category: KeywordType) => {
    try {
      const payload = {
        keyword: keyword,
        type: category,
      };

      const { result, status } = await api(
        `admin/v1/add_new_keyword`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        },
        { token: auth?.accessToken, refresh },
        'site-intelligence',
      ).then(async (resp) => {
        const result = await resp?.json();
        return { result: result, status: result?.code ?? resp?.status };
      });
      const resolvedResult = await result;
      if (status === 200 && resolvedResult.code === 200 && resolvedResult.error === false) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      return false;
    }
  };

  const onSubmit: SubmitHandler<typeof defaultValues> = async (formData) => {
    setIsLoading(true);
    setErrorMessage('');

    const service = SiService?.[formData?.category] ?? SiService.CATEGORIZE_SERVICE;

    try {
      // Parse and update draft data
      if (service === SiService.MEMBERS_ONLY_SIFT_SERVICE) {
        onUpdate(service, 'result.members_only', true);
      } else if (service === SiService.ADULT_SHIELD_SERVICE) {
        const captureIds = await Promise.all(
          formData.sexualCategories.map((cat) =>
            saveCapture({
              file: cat.screenshot,
              service,
              requestId: data[service].request_id,
              queueId: data[service].queue_id,
              url: formData.url,
            })
          )
        );
        if (captureIds.some((item) => !item)) {
          throw new Error('Fail to save screenshot.');
        }

        onUpdate(service, (currentDraft) => {
          const defaultKeywordForAdult = {
            keyword_original: '',
            keyword_en: '',
            language: '',
            field_status: FieldStatus.ACTIVE,
          };
          const existingUrlObj = currentDraft?.detail?.find((item) => item.url === formData.url);
          if (existingUrlObj) {
            formData.sexualCategories.forEach((sexualCategory, index) => {
              const categoryObj = existingUrlObj.categories?.find(
                (c) => c.category === sexualCategory.name
              );
              if (categoryObj) {
                categoryObj.capture_id = captureIds[index] ?? categoryObj.capture_id;
                categoryObj.keywords?.forEach((k) => {
                  k.field_status = FieldStatus.ACTIVE;
                });
              } else {
                existingUrlObj.categories?.push({
                  capture_id: captureIds[index] ?? null,
                  category: sexualCategory.name,
                  keywords: [defaultKeywordForAdult],
                });
              }
            });
          } else {
            currentDraft?.detail?.push({
              capture_id: null,
              url: formData.url,
              categories: formData.sexualCategories.map((sexualCategory, index) => ({
                capture_id: captureIds[index] ?? null,
                category: sexualCategory.name,
                keywords: [defaultKeywordForAdult],
              })),
            });
          }

          // Update sexual categories in result
          formData.sexualCategories.forEach((sexualCategory) => {
            const existingCategoryInResult = currentDraft?.result?.categories?.find(
              (c) => c.category === sexualCategory.name
            );
            if (existingCategoryInResult) {
              existingCategoryInResult.field_status = FieldStatus.ACTIVE;
            } else {
              currentDraft?.result?.categories?.push({
                category: sexualCategory.name,
                field_status: FieldStatus.ACTIVE,
              });
            }
          });
          // Ensure the adult toggle/result flag is set so the UI keeps the section visible
          if (!currentDraft.result) currentDraft.result = {};
          currentDraft.result.adult = true;
        });
      } else {
        const captureId = await saveCapture({
          file: formData.screenshot,
          service,
          requestId: data[service].request_id,
          queueId: data[service].queue_id,
          url: formData.url,
        });
        if (!captureId) {
          throw new Error('Fail to save screenshot.');
        }

        if (service === SiService.TOBACCO_SIFT_SERVICE) {
          const newCubanBrands = formData?.brands?.filter((brand) => brand?.isNew);
          if (newCubanBrands?.length > 0) {
            const addNewCubanBrandResults = await Promise.all(
              newCubanBrands.map((brand) => handleAddNewCubanBrand(brand?.name))
            );
            if (addNewCubanBrandResults.some((result) => !result)) {
              throw new Error('Fail to add new cuban cigar brand/s.');
            }
          }

          onUpdate(service, (currentDraft) => {
            const urlObj = currentDraft?.detail?.find((item) => item.url === formData.url);
            if (urlObj) {
              urlObj.capture_id = captureId ?? urlObj.capture_id;

              formData.brands?.forEach((brand) => {
                const existingBrandObj = urlObj.cuban_cigar_brands?.find(
                  (b) => b.brand === brand.name
                );
                if (existingBrandObj) {
                  existingBrandObj.field_status = FieldStatus.ACTIVE;
                } else {
                  urlObj.cuban_cigar_brands?.push({
                    brand: brand.name,
                    field_status: FieldStatus.ACTIVE,
                  });
                }
              });
            } else {
              currentDraft?.detail?.push({
                capture_id: captureId ?? null,
                url: formData.url,
                cuban_cigar_brands: formData.brands?.map((brand) => ({
                  brand: brand.name,
                  field_status: FieldStatus.ACTIVE,
                })),
                tobacco_keywords: [],
              });
            }

            // Update cuban cigar brands in result
            if (!currentDraft.result) currentDraft.result = {};
            currentDraft.result.tobacco = true;
            if (!currentDraft.result.cuban_cigar_brands) {
              currentDraft.result.cuban_cigar_brands = [];
            }

            formData.brands.forEach((brand) => {
              const existingCubanBrandInResult = currentDraft?.result?.cuban_cigar_brands?.find(
                (c) => c.brand === brand.name
              );
              if (existingCubanBrandInResult) {
                existingCubanBrandInResult.field_status = FieldStatus.ACTIVE;
              } else {
                currentDraft?.result?.cuban_cigar_brands?.push({
                  category: brand.name,
                  field_status: FieldStatus.ACTIVE,
                });
              }
            });

            if ((currentDraft?.result?.cuban_cigar_brands?.length ?? 0) > 0) {
              currentDraft.result.cuban = true;
            }
          });
        } else if (service === SiService.CATEGORIZE_SERVICE) {
          const newKeywords = formData?.keywords?.filter((keyword) => keyword?.isNew);
          if (newKeywords?.length > 0) {
            const addNewKeywordResults = await Promise.all(
              newKeywords.map((keyword) =>
                handleAddNewKeyword(keyword?.name, KeywordType[formData?.category])
              )
            );
            if (addNewKeywordResults.some((result) => !result)) {
              throw new Error('Fail to add new categorize keyword/s.');
            }
          }

          onUpdate(service, (currentDraft) => {
            const urlObjFromFormData = {
              capture_id: captureId ?? null,
              url: formData.url,
              keywords: formData.keywords?.map((keyword) => ({
                keyword: keyword.name,
                field_status: FieldStatus.ACTIVE,
                snippets: [],
              })),
            };
            const categoriesDetail = currentDraft?.detail?.find(
              (item) => item.section === CategorizeServiceSection.CATEGORY
            )?.section_detail;
            const existingCategoryObj = categoriesDetail?.find(
              (c) => c.category === formData.category
            );

            if (existingCategoryObj) {
              const urlObj = existingCategoryObj?.url_detail?.find((u) => u.url === formData.url);
              if (urlObj) {
                urlObj.capture_id = captureId ?? urlObj.capture_id;
                formData.keywords?.forEach((keyword) => {
                  const existingKeywordObj = urlObj.keywords?.find(
                    (k) => k.keyword === keyword.name
                  );
                  if (existingKeywordObj) {
                    existingKeywordObj.field_status = FieldStatus.ACTIVE;
                  } else {
                    urlObj.keywords?.push({
                      keyword: keyword.name,
                      field_status: FieldStatus.ACTIVE,
                      snippets: [],
                    });
                  }
                });
              } else {
                existingCategoryObj.url_detail?.push(urlObjFromFormData);
              }
            } else {
              categoriesDetail?.push({
                category: formData.category,
                url_detail: [urlObjFromFormData],
              });
            }

            // Update result
            const existingCategoryInResult = currentDraft?.result?.categories?.find(
              (c) => c.category === formData.category
            );
            if (existingCategoryInResult) {
              existingCategoryInResult.field_status = FieldStatus.ACTIVE;
            } else {
              currentDraft?.result?.categories?.push({
                category: formData.category,
                field_status: FieldStatus.ACTIVE,
              });
            }
          });
        }
      }

      // Save draft data
      await onSaveMultiple([
        {
          selector: service,
          ...(service === SiService.CATEGORIZE_SERVICE && {
            paths: [
              `detail[${data?.[SiService.CATEGORIZE_SERVICE]?.detail?.findIndex(
                (item) => item.section === CategorizeServiceSection.CATEGORY
              )}]`,
              'result.categories',
            ],
          }),
        },
      ]);

      handleClose();
    } catch (err) {
      setToast({
        show: true,
        type: 'Danger',
        header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
        message: 'Fail to add category.',
      });
      setErrorMessage(err?.message || intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='d-flex flex-row-fluid flex-center bg-white rounded'>
      <form className='pt-5 w-100 w-xl-900px px-md-9' onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className='d-flex mb-4 align-items-center'>
          <label className='form-label required w-50'>Category</label>
          <Controller
            name='category'
            control={control}
            render={({ field, fieldState }) => {
              return (
                <Select
                  {...field}
                  placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                  className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''}`}
                  onChange={(e) => {
                    field.onChange(e?.value);
                  }}
                  options={categoryOptions}
                  value={categoryOptions.find((option) => option.value === field.value)}
                  isDisabled={isLoading}
                />
              );
            }}
          />
        </div>

        <div className='d-flex mb-4 align-items-center'>
          <label className='form-label required w-50'>
            URL <small>(must include http:// or https://)</small>
          </label>
          <Controller
            name='url'
            control={control}
            render={({ field, fieldState }) => {
              return (
                <input
                  {...field}
                  disabled={
                    isLoading ||
                    !selectedCategory ||
                    selectedCategory === SiService.MEMBERS_ONLY_SIFT_SERVICE
                  }
                  type='text'
                  className={clsx(
                    'form-control fw-normal fs-base',
                    fieldState.invalid && 'border border-danger'
                  )}
                  placeholder='https://example.com'
                />
              );
            }}
          />
        </div>

        {/* Adult-specific Fields */}
        {selectedCategory === SiService.ADULT_SHIELD_SERVICE && (
          <>
            <div className='separator my-6'></div>
            <div className='w-100'>
              {fields.map((field, index) => (
                // key={field.id} to ensure proper update on remove row
                <div className='d-inline-block' key={field.id}>
                  <div className='d-flex justify-content-between align-items-start gap-4 mb-1'>
                    <div className='w-100px w-md-350px'>
                      {index === 0 && (
                        <label className='form-label  required'>Sexual Category</label>
                      )}
                      <Controller
                        name={`sexualCategories.${index}.name`}
                        control={control}
                        render={({ field, fieldState }) => {
                          return (
                            <Select
                              {...field}
                              placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                              className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''
                                }`}
                              onChange={(e) => {
                                field.onChange(e?.value);
                              }}
                              options={sexualCategoryOptions}
                              value={sexualCategoryOptions.find(
                                (option) => option.value === field.value
                              )}
                              isDisabled={isLoading}
                            />
                          );
                        }}
                      />
                    </div>

                    <div className='w-100px w-md-250px'>
                      {index === 0 && <label className='form-label required'>Screenshot</label>}
                      <Controller
                        name={`sexualCategories.${index}.screenshot`}
                        control={control}
                        render={({ field, fieldState }) => {
                          return (
                            <PreviewDropzoneWithPaste
                              file={field.value instanceof File ? field.value : null}
                              onFileChange={(file) => {
                                field.onChange(file || null);
                              }}
                              acceptFileTypes={acceptedFileTypes}
                              maxSize={acceptedFileSize}
                              subLabel={
                                <div className='form-text mt-1'>
                                  png, jpg, jpeg
                                  <span className='bullet bullet-dot mx-2 align-middle'> </span>
                                  max {bytes(acceptedFileSize)}
                                </div>
                              }
                              error={fieldState.error?.message}
                              className={clsx(isLoading && 'opacity-75')}
                              previewItemWidth='100%'
                              previewItemHeight='100px'
                              hideErrorMessage='external'
                              isMini
                            />
                          );
                        }}
                      />
                    </div>

                    <div className='d-flex align-items-center'>
                      <i
                        className={clsx(
                          'bi bi-dash-square-fill fs-1',
                          index > 0 ? 'text-danger cursor-pointer' : 'mt-8'
                        )}
                        onClick={() => {
                          if (index > 0) {
                            remove(index);
                          }
                        }}
                      ></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className='my-1'>
              <i
                className='bi bi-plus-square-fill fs-1 text-success cursor-pointer'
                onClick={() => {
                  append({
                    ...defaultValues.sexualCategories[0],
                    screenshot: defaultScreenshotFile,
                  });
                }}
              ></i>
            </div>
          </>
        )}

        {/* Screenshot */}
        {(selectedCategory === SiService.TOBACCO_SIFT_SERVICE || isKeywordCategory) && (
          <div className='d-flex mb-4 align-items-center'>
            <label className='form-label required w-50'>Screenshot</label>
            <Controller
              name='screenshot'
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <PreviewDropzoneWithPaste
                    file={field.value instanceof File ? field.value : null}
                    onFileChange={(file) => {
                      field.onChange(file || null);
                    }}
                    acceptFileTypes={acceptedFileTypes}
                    maxSize={acceptedFileSize}
                    error={fieldState.error?.message}
                    containerClassName='w-100'
                    className={clsx('min-h-100px', isLoading && 'opacity-75')}
                  />
                );
              }}
            />
          </div>
        )}

        {/* Tobacco Brands */}
        {selectedCategory === SiService.TOBACCO_SIFT_SERVICE && (
          <div className='d-flex mb-4 align-items-center'>
            <label className='form-label w-50'>Cuban Cigar Brands</label>
            <Controller
              name='brands'
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <CreatableSelect
                    {...field}
                    isMulti
                    placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                    noOptionsMessage={() => intl.formatMessage({ id: 'COMMON.SELECT' })}
                    className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''}`}
                    options={cubanBrandOptions}
                    value={field.value.map((value) => {
                      return { label: value.name, value: value.name, __isNew__: value.isNew };
                    })}
                    onChange={(selectedOptions) => {
                      field.onChange(
                        selectedOptions.map(
                          (option: { label: string; value: string; __isNew__: boolean }) => ({
                            name: option.value,
                            isNew: option.__isNew__,
                          })
                        )
                      );
                    }}
                    isDisabled={isLoading}
                  />
                );
              }}
            />
          </div>
        )}

        {/* Categorize Keywords */}
        {isKeywordCategory && (
          <div className='d-flex mb-4 align-items-center'>
            <label className='form-label w-50'>Keywords</label>
            <Controller
              name='keywords'
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <CreatableSelect
                    {...field}
                    isMulti
                    placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                    noOptionsMessage={() => intl.formatMessage({ id: 'COMMON.SELECT' })}
                    className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''}`}
                    options={categorizeKeywordOptions}
                    value={field.value.map((value) => {
                      return { label: value.name, value: value.name, __isNew__: value.isNew };
                    })}
                    onChange={(selectedOptions) => {
                      field.onChange(
                        selectedOptions.map(
                          (option: { label: string; value: string; __isNew__: boolean }) => ({
                            name: option.value,
                            isNew: option.__isNew__,
                          })
                        )
                      );
                    }}
                    isDisabled={isLoading}
                  />
                );
              }}
            />
          </div>
        )}

        {errorMessage && (
          <div className='text-danger my-4 border border-danger rounded p-2'>{errorMessage}</div>
        )}

        <div className='d-flex justify-content-end mt-4'>
          <button
            className='btn btn-primary d-flex align-items-center gap-2'
            type='submit'
            disabled={isLoading}
          >
            {intl.formatMessage({ id: 'COMMON.SUBMIT' })}
            {isLoading ? (
              <span className='spinner-border spinner-border-sm align-middle'></span>
            ) : (
              <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3 m-0' />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export { AddCategory };
